import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { Currency } from '@core/dto/CurrencyCode';
import { PaginatedList } from '@core/dto/PaginatedList';
import { Refund } from '@core/dto/refund/Refund';
import { RefundStatus } from '@core/params';
import { AppStateService } from '@core/service/app-state.service';
import { AuthenticationService } from '@core/service/authentication.service';
import { CustomizationService } from '@core/service/customization.service';
import { RefundService } from '@core/service/refund.service';
import { StoreService } from '@core/service/store.service';
import { ValueListPipe } from '@shared/pipe/value.pipe';
import { EmptyState } from '@zfb/ui/empty-state/empty-state.component';
import {
  ColumnDefinition,
  FilterOption,
  TableRow,
  TableRows,
} from '@zfb/ui/table/table.component';
import { QuickAction } from '@zfb/ui/quick-actions/quick-actions.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ColorService } from '@core/service/color.service';
import { PageTab } from '@zfb/ui/page-tabs/page-tabs.component';
import { TopUpService } from '@core/service/top-up.service';
import { LocaleService } from '@core/service/locale.service';
import { PaginationState } from '@zfb/ui/table-pagination/table-pagination.component';
import { Permission } from '@core/dto/user-details';
import { AccessControlService } from '@core/service/access-control.service';

export enum RefundPage {
  REPORTED = 'REPORTED',
  MANAGED = 'MANAGED',
}
@Component({
  selector: 'app-refunds',
  templateUrl: './refunds.component.html',
  styleUrls: ['../page-shared.scss', './refunds.component.css'],
})
export class RefundsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  constructor(
    private refundService: RefundService,
    auth: AuthenticationService,
    private storeService: StoreService,
    private appStateService: AppStateService,
    private customizationService: CustomizationService,
    public colorService: ColorService,
    public localeService: LocaleService,
    private access: AccessControlService,
    public topUpService: TopUpService
  ) {
    super(auth);

    this.searchQueryChanged
      .pipe(
        map((q) => q.trim()),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((query: string) => {
        this.paginationState.page = 1;
        this.searchQuery = query;
        this.fetchRefunds();
      });
  }

  defaultManagedPageStatusFilters: RefundStatus[] = [
    RefundStatus.PENDING_EXECUTION,
    RefundStatus.EXECUTED,
    RefundStatus.EXECUTION_INITIATED,
    RefundStatus.CANCELLED,
    RefundStatus.CANCELLED_BY_SYSTEM,
    RefundStatus.ERROR,
  ];

  columns: ColumnDefinition<Refund, RefundStatus>[] = [
    {
      cssClasses: 'medium2 avoid-overflow first-column',
      columnDataTransformer: (refund) => refund.paymentRequestTitle,
      text: $localize`:@@refunds.columns.title:Title`,
    },
    {
      columnDataTransformer: (refund) =>
        new DatePipe(this.localeService.getCurrentLocale()).transform(
          refund.created,
          'd MMM y'
        ),
      text: $localize`:@@refunds.columns.reportedDate:Reported`,
    },
    {
      cssClasses: 'medium2',
      columnDataTransformer: (refund) =>
        new ValueListPipe().transform(
          refund.paymentRequestValue,
          this.localeService.getCurrentLocale()
        ),
      columnHeadingStylingTransformer: () => 'width: 170px;',
      columnStylingTransformer: () => 'width: 170px;',
      text: $localize`:@@refunds.columns.originalAmount:Initial amount`,
    },
    {
      cssClasses: 'medium2 cerise-text',
      columnDataTransformer: (refund) => {
        const refundValue: Currency = {
          amount:
            refund.refundValue.amount > 0
              ? -refund.refundValue.amount
              : refund.refundValue.amount,
          currencyCode: refund.refundValue.currencyCode,
        };
        return new ValueListPipe().transform(
          refundValue,
          this.localeService.getCurrentLocale()
        );
      },
      columnHeadingStylingTransformer: () => 'width: 170px;',
      columnStylingTransformer: () => 'width: 170px;',
      text: $localize`:@@refunds.columns.refund:Refund`,
    },
    {
      filterable: false,
      type: 'status',
      text: $localize`:@@refunds.columns.status.text:Status`,
      columnDataTransformer: (refund) => refund.statusText,
      filterOptions: {
        text: $localize`:@@refunds.columns.status.filterOptions.text:Status`,
        value: 'status',
        activeFilters: [],
        options: [
          {
            headerText: $localize`:@@refunds.columns.status.filterOptions.all.headerText:all`,
            displayText: $localize`:@@refunds.columns.status.filterOptions.all.displayText:Show all`,
            value: [],
          },
          {
            headerText: $localize`:@@refunds.columns.status.filterOptions.approved.headerText:approved`,
            displayText: $localize`:@@refunds.columns.status.filterOptions.approved.displayText:Approved`,
            value: [
              RefundStatus.PENDING_EXECUTION,
              RefundStatus.EXECUTED,
              RefundStatus.EXECUTION_INITIATED,
            ],
          },
          {
            headerText: $localize`:@@refunds.columns.status.filterOptions.denied.headerText:denied`,
            displayText: $localize`:@@refunds.columns.status.filterOptions.denied.displayText:Denied`,
            value: [RefundStatus.CANCELLED],
          },
          {
            headerText: $localize`:@@refunds.columns.status.filterOptions.cancelled.headerText:cancelled`,
            displayText: $localize`:@@refunds.columns.status.filterOptions.cancelled.displayText:Cancelled`,
            value: [RefundStatus.CANCELLED_BY_SYSTEM, RefundStatus.ERROR],
          },
        ],
      },
    },
  ];

  // Todo: implement this when the mobile view is designed
  refunds: TableRows<Refund, RefundStatus> = {
    getPrimaryText: (refund) =>
      new ValueListPipe().transform(
        refund.refundValue,
        this.localeService.getCurrentLocale()
      ),
    getSecondaryText: (refund) => refund.paymentRequestTitle,
    getStatusBarColor: (refund) => refund.statusColor,
    getBackgroundColor: (refund) => refund.backgroundColor,
    rows: [],
    hideMobileHeader: false,
    rowClickFunction: (refund) => this.openViewRefundModal(refund),
  };

  quickActions: QuickAction<Refund>[] = [
    {
      text: $localize`:@@refunds.quickActions.approveRefund.text:Approve refund`,
      iconUrl: 'assets/icons/Icon-Checkmark-Circle.svg',
      textColor: '#0A1018',
      disabled: false,
      displayCondition: (refund) => {
        return (
          refund.status === RefundStatus.PENDING_MERCHANT_APPROVAL &&
          this.activePage === RefundPage.REPORTED &&
          this.access.userMay(Permission.APPROVE_REFUND) &&
          this.refundService.currentUserMayApproveRefund(refund)
        );
      },
      topBorder: false,
      function: (refund) => this.openApprovalConfirmation(refund, true),
    },
    {
      text: $localize`:@@refunds.quickActions.denyRefund.text:Deny refund`,
      iconUrl: 'assets/icons/Icon-Delete-Cerise.svg',
      textColor: this.colorService.getCeriseColorCode(),
      disabled: false,
      displayCondition: (refund) =>
        refund.status === RefundStatus.PENDING_MERCHANT_APPROVAL &&
        this.activePage === RefundPage.REPORTED &&
        this.access.userMay(Permission.CANCEL_REFUND),
      topBorder: false,
      function: (refund) => {
        this.openCancellingConfirmation(refund, true);
      },
    },
  ];

  invokedFromList = false;

  topUpData: string | null;
  RefundPage = RefundPage;
  activePage = RefundPage.REPORTED;
  tabs: PageTab<RefundPage>[] = [
    {
      value: RefundPage.REPORTED,
      label: $localize`:@@refunds.tabs.reported:Reported refunds`,
    },
    {
      value: RefundPage.MANAGED,
      label: $localize`:@@refunds.tabs.managed:Managed`,
    },
  ];

  selectedRefund: Refund;
  merchantHasRefunds = false;

  viewRefund = false;
  confirmApproval = false;
  confirmDeclining = false;

  modalActive = false;
  smallModalActive = false;
  modalColor: string;
  modalTitle: string;

  fetchingRefunds = true;
  fetchingStores = true;
  searchQuery = '';
  searchQueryChanged: Subject<string> = new Subject<string>();
  statusFilters: RefundStatus[] = [RefundStatus.PENDING_MERCHANT_APPROVAL];

  storesFilter = '';
  storesFilterOptions: { label: string; value: string }[] = null;

  paginationState: PaginationState = {
    page: 1,
    pageSize: 10,
    total: 0,
    numOfItems: 0,
  };

  displayEmptyState = false;
  currentEmptyState: EmptyState;
  noRefundsAtAllEmptyState: EmptyState = {
    imgSrc: 'assets/empty-states/Waving-Hand.png',
    headingText: $localize`:@@refunds.emptyState.noRefundsAtAllEmptyState.headingText:Welcome&nbsp;to&nbsp;refunds!`,
    bodyText: $localize`:@@refunds.emptyState.noRefundsAtAllEmptyState.bodyText:As soon as a refund is created it will show&nbsp;up&nbsp;here.`,
  };
  noReportedRefundsEmptyState: EmptyState = {
    imgSrc: 'assets/empty-states/Girl-Laptop.png',
    headingText: $localize`:@@refunds.emptyState.noReportedRefundsEmptyState.headingText:No&nbsp;action&nbsp;needed&nbsp;here`,
    bodyText: $localize`:@@refunds.emptyState.noReportedRefundsEmptyState.bodyText:There are currently no refunds that need your attention&nbsp;right&nbsp;now.`,
  };
  noManagedRefundsEmptyState: EmptyState = {
    imgSrc: 'assets/empty-states/Female-Detective.png',
    headingText: $localize`:@@refunds.emptyState.noManagedRefundsEmptyState.headingText:Manage&nbsp;refunds`,
    bodyText: $localize`:@@refunds.emptyState.noManagedRefundsEmptyState.bodyText:Here you can see all managed refunds so you can easily find them and go back&nbsp;through&nbsp;history.`,
  };
  noRefundsSearchedEmptyState: EmptyState = {
    imgSrc: 'assets/empty-states/Emoji-Monocle.png',
    headingText: $localize`:@@refunds.emptyState.noRefundsSearchedEmptyState.headingText:We couldn't find a match for&nbsp;search`,
    bodyText: $localize`:@@refunds.emptyState.noRefundsSearchedEmptyState.bodyText:Give it another attempt. Try&nbsp;again.`,
  };
  noRefundsFilteredEmptyState: EmptyState = {
    imgSrc: 'assets/empty-states/Emoji-Ghost.png',
    headingText: $localize`:@@refunds.emptyState.noRefundsFilteredEmptyState.bodyText:There is nothing here&nbsp;yet`,
  };

  isTopUp = false;
  statePushedToHistory = false;

  // Event listener for closing full screen modals on backpress
  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    if (this.deviceIsMobile()) {
      if (this.statePushedToHistory) {
        this.statePushedToHistory = false;
      }
      if (this.modalActive) {
        this.closeModal();
      } else if (this.smallModalActive) {
        this.returnToViewModal(this.selectedRefund);
      }
    }
  }

  ngOnInit(): void {
    if (window.location.hash === '#modal') {
      history.back();
    }
    this.fetchRefunds();

    if (this.appStateService.repHasStoreAccess()) {
      this.setupStores();
    }
    window.onbeforeunload = () => this.ngOnDestroy();

    this.topUpBalance();
  }

  topUpBalance() {
    this.topUpService.getTopUpBalance().subscribe((res) => {
      if (res !== null) {
        this.topUpData = `${res.toLocaleString('sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} kr`;
        this.isTopUp = true;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.deviceIsMobile()) {
      if (this.modalActive) {
        this.closeModal();
      } else if (this.smallModalActive) {
        this.closeSmallModal();
      }
    }
  }

  fetchRefunds() {
    this.fetchingRefunds = true;
    this.displayEmptyState = false;
    this.refundService
      .search(
        this.searchQuery,
        this.statusFilters,
        this.storesFilter,
        this.paginationState.page - 1, // page-numbering is zero based on the server
        this.paginationState.pageSize
      )
      .subscribe(
        (res) => {
          this.updateListWithResponse(res);
          this.fetchingRefunds = false;
        },
        (error: HttpErrorResponse) => {
          this.handleError(error);
          this.fetchingRefunds = false;
        }
      );
  }

  updateListWithResponse(res: PaginatedList<Refund>) {
    const refunds = res.elements.map(Refund.setup);
    if (this.activePage === RefundPage.REPORTED) {
      this.refunds.rows = refunds.map((refund) =>
        this.setupQuickActionsForRefund(refund)
      );
    } else {
      this.refunds.rows = refunds.map((refund) => ({
        data: refund,
        quickActions: null,
      }));
    }
    this.paginationState.numOfItems = this.refunds.rows.length;
    this.paginationState.page = res.page + 1; // page-numbering is zero based on the server
    this.paginationState.total = res.total;
    if (this.paginationState.numOfItems === 0) {
      this.setEmptyState();
    } else {
      this.merchantHasRefunds = true;
    }
    this.fetchingRefunds = false;
  }

  setupQuickActionsForRefund(refund: Refund): TableRow<Refund> {
    return {
      data: refund,
      quickActions: this.quickActions.filter((action) =>
        action.displayCondition(refund)
      ),
    };
  }

  setEmptyState() {
    if (
      (this.activePage === RefundPage.REPORTED && this.storesFilter !== '') ||
      (this.activePage === RefundPage.MANAGED &&
        (this.statusFilters !== this.defaultManagedPageStatusFilters ||
          this.storesFilter.length))
    ) {
      // No refunds returned after filtering
      this.currentEmptyState = this.noRefundsFilteredEmptyState;
    } else if (this.searchQuery !== '') {
      // No refunds returned after searching
      this.currentEmptyState = this.noRefundsSearchedEmptyState;
    } else {
      // No refunds retured despite no filtering and no searching
      if (this.activePage === RefundPage.MANAGED) {
        this.currentEmptyState = this.noManagedRefundsEmptyState;
      } else {
        if (this.merchantHasRefunds) {
          this.currentEmptyState = this.noReportedRefundsEmptyState;
          this.displayEmptyState = true;
        } else {
          // We need to know if there exists any refunds at all
          this.refundService.search().subscribe((res) => {
            if (res.total === 0) {
              // No refunds exists at all
              this.currentEmptyState = this.noRefundsAtAllEmptyState;
            } else {
              // There exist refunds, but none that that have the status PENDING_MERCHANT_APPROVAL
              this.currentEmptyState = this.noReportedRefundsEmptyState;
              this.merchantHasRefunds = true;
            }
            this.displayEmptyState = true;
          });
          return;
        }
      }
    }
    this.displayEmptyState = true;
  }

  setupStores() {
    this.fetchingStores = true;
    this.storeService
      .get()
      .then((res) => {
        if (res.length > 0) {
          this.storesFilterOptions = [
            {
              // prettier-ignore
              label: $localize`:@@refunds.storesFilterOptions.all.label:Show all ${this.getStoreAlias(true)}`,
              value: '',
            },
          ];
          res.forEach((store) =>
            this.storesFilterOptions.push({
              label: store.name,
              value: store.id,
            })
          );
        }
        this.fetchingStores = false;
      })
      .catch((error) => {
        this.handleError(error);
        this.fetchingStores = false;
      });
  }

  getStoreAlias(plural: boolean): string {
    return this.customizationService.getStoreAlias(plural);
  }

  setActivePage(page: RefundPage) {
    if (this.activePage === page) {
      return;
    }

    this.activePage = page;
    if (this.activePage === RefundPage.REPORTED) {
      this.statusFilters = [RefundStatus.PENDING_MERCHANT_APPROVAL];
      this.columns[this.columns.length - 1].filterable = false;
    } else {
      this.statusFilters = this.defaultManagedPageStatusFilters;
      this.columns[this.columns.length - 1].filterable = true;
    }
    this.columns[this.columns.length - 1].filterOptions.activeFilters = [];
    this.paginationState.page = 1;
    this.fetchRefunds();
  }

  onStoresFilterChange(event: string) {
    this.storesFilter = event;
    this.paginationState.page = 1;
    this.fetchRefunds();
  }

  onFilterChange(event: {
    type: string;
    filterOption: FilterOption<RefundStatus>;
  }) {
    this.statusFilters = event.filterOption.value.length
      ? event.filterOption.value
      : this.defaultManagedPageStatusFilters;
    const column = this.columns.find((col) => col.type === event.type);
    column.filterOptions.activeFilters = this.statusFilters;
    this.paginationState.page = 1;
    this.fetchRefunds();
  }

  onPageChange(page: number) {
    this.paginationState.page = page;
    this.fetchRefunds();
  }

  onPageSizeChange(pageSize: number) {
    this.paginationState.page = 1;
    this.paginationState.pageSize = pageSize;
    this.fetchRefunds();
  }

  /*
  Modal functions
  */
  onHandled() {
    this.closeModal();
    this.closeSmallModal();
    this.fetchRefunds();
  }

  openViewRefundModal(refund: Refund) {
    this.selectedRefund = refund;
    this.openModal(refund);
  }

  returnToViewModal(refund: Refund) {
    this.closeSmallModal();
    if (this.invokedFromList) {
      this.invokedFromList = false;
      return;
    }
    this.openViewRefundModal(refund);
  }

  openApprovalConfirmation(refund: Refund, invokedFromList: boolean) {
    this.invokedFromList = invokedFromList;
    this.closeModal();
    this.selectedRefund = refund;
    this.openSmallModal('approve');
  }

  openCancellingConfirmation(refund: Refund, invokedFromList: boolean) {
    this.invokedFromList = invokedFromList;
    this.closeModal();
    this.selectedRefund = refund;
    this.openSmallModal('cancel');
  }

  openSmallModal(modalType: 'approve' | 'cancel') {
    switch (modalType) {
      case 'approve':
        this.modalColor = '#00A880';
        this.modalTitle = $localize`:@@refunds.modal.approve.modalTitle:Approve refund`;
        this.confirmApproval = true;
        break;
      case 'cancel':
        this.modalColor = this.colorService.getCeriseColorCode();
        this.modalTitle = $localize`:@@refunds.modal.deny.modalTitle:Deny refund`;
        this.confirmDeclining = true;
        break;
    }
    this.smallModalActive = true;
    if (!this.statePushedToHistory && this.deviceIsMobile()) {
      history.pushState(
        null,
        null,
        `/${this.localeService.getCurrentLocaleBaseCode()}/admin/refunds#modal`
      );
      this.statePushedToHistory = true;
    }
  }

  closeSmallModal() {
    this.smallModalActive = false;
    this.confirmApproval = false;
    this.confirmDeclining = false;
  }

  openModal(refund: Refund) {
    if (this.activePage === RefundPage.REPORTED) {
      this.modalColor = '#4466EE';
      this.modalTitle = $localize`:@@refunds.modal.reported.modalTitle:Reported refund`;
    } else {
      this.modalColor = refund.statusColor;
      this.modalTitle = $localize`:@@refunds.modal.managed.modalTitle:${refund.statusText} refund`;
    }
    this.viewRefund = true;
    this.modalActive = true;
    if (!this.statePushedToHistory && this.deviceIsMobile()) {
      history.pushState(
        null,
        null,
        `/${this.localeService.getCurrentLocaleBaseCode()}/admin/refunds#modal`
      );
      this.statePushedToHistory = true;
    }
  }

  closeModal() {
    this.modalActive = false;
    this.viewRefund = false;
    if (this.statePushedToHistory && this.deviceIsMobile()) {
      history.back();
    }
  }

  deviceIsMobile() {
    return window.innerWidth <= 520;
  }
}
